import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/lib/supabase"

type ViewSyncPayload = {
  globalDelta?: number
  institutionId?: string | null
  institutionDelta?: number
  modulePageId?: string | null
  moduleDelta?: number
}

let viewSyncChannel: any | null = null

export function broadcastViewUpdate(payload: ViewSyncPayload) {
  try {
    if (!viewSyncChannel) return
    void viewSyncChannel.send({
      type: "broadcast",
      event: "view_update",
      payload,
    })
  } catch {
    void 0
  }
}

export type LivePageCounters = {
  totalViews: number
  uniqueUsers: number
  sessionCount: number
}

export type CurrentViewKey = {
  pageType: "home" | "module" | null
  pageId: string | null
}

type CountersContextValue = {
  currentPage: LivePageCounters | null
  setCurrentPage: (value: LivePageCounters | null) => void
  optimisticBumpView: (payload: ViewSyncPayload) => void
  currentView: CurrentViewKey
  setCurrentView: (next: CurrentViewKey) => void
}

const CountersContext = createContext<CountersContextValue | undefined>(undefined)

export function CountersProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState<LivePageCounters | null>(null)
  const [currentView, setCurrentView] = useState<CurrentViewKey>({ pageType: null, pageId: null })
  const applyDelta = useCallback(
    (payload: ViewSyncPayload) => {
      const globalDelta = payload.globalDelta ?? 0
      const institutionId = typeof payload.institutionId === "string" ? payload.institutionId.trim().toLowerCase() : null
      const institutionDelta = payload.institutionDelta ?? 0
      const modulePageId = typeof payload.modulePageId === "string" ? payload.modulePageId.trim() : null
      const moduleDelta = payload.moduleDelta ?? 0

      if (globalDelta) {
        queryClient.setQueryData(["views", "global"], (prev) => (typeof prev === "number" ? prev : 0) + globalDelta)
        void queryClient.invalidateQueries({ queryKey: ["views", "global"] })
        void queryClient.refetchQueries({ queryKey: ["views", "global"], type: "active" })
      }
      if (institutionId && institutionDelta) {
        queryClient.setQueryData(["views", "institution", institutionId], (prev) => (typeof prev === "number" ? prev : 0) + institutionDelta)
        void queryClient.invalidateQueries({ queryKey: ["views", "institution", institutionId] })
        void queryClient.refetchQueries({ queryKey: ["views", "institution", institutionId], type: "active" })
      }
      if (modulePageId && moduleDelta) {
        queryClient.setQueryData(["views", "module", modulePageId], (prev) => (typeof prev === "number" ? prev : 0) + moduleDelta)
        void queryClient.invalidateQueries({ queryKey: ["views", "module", modulePageId] })
        void queryClient.refetchQueries({ queryKey: ["views", "module", modulePageId], type: "active" })
      }
    },
    [queryClient],
  )

  const optimisticBumpView = useCallback((payload: ViewSyncPayload) => {
    applyDelta(payload)
  }, [applyDelta])

  const subscribedRef = useRef(false)
  useEffect(() => {
    if (subscribedRef.current) return
    subscribedRef.current = true

    try {
      const chFactory = (supabase as any)?.channel
      if (typeof chFactory !== "function") return
      const channel = supabase.channel("views_sync_v1")
      viewSyncChannel = channel
      channel.on("broadcast", { event: "view_update" }, (msg: any) => {
        const payload = (msg?.payload || {}) as ViewSyncPayload
        applyDelta(payload)
      })
      channel.subscribe()
      return () => {
        try {
          if (viewSyncChannel === channel) viewSyncChannel = null
          void channel.unsubscribe()
        } catch {
          void 0
        }
      }
    } catch {
      return
    }
  }, [applyDelta])

  const value = useMemo(
    () => ({
      currentPage,
      setCurrentPage,
      optimisticBumpView,
      currentView,
      setCurrentView,
    }),
    [currentPage, optimisticBumpView, currentView]
  )

  return <CountersContext.Provider value={value}>{children}</CountersContext.Provider>
}

export function useCounters() {
  const ctx = useContext(CountersContext)
  if (!ctx) throw new Error("useCounters must be used within a CountersProvider")
  return ctx
}
